<script>
import { format, subDays, addDays, parseISO } from "date-fns";
import Chart from "vue-apexcharts";
import Spinner from "@/components/ui/Spinner";
import ordersCrmService from "@/services/orders-crm";
import { STATUS_META, statusLabel } from "@/services/orders-crm-statuses";

// Tiene que coincidir con el máximo que acepta la API (STATS_MAX_RANGE_DAYS).
const MAX_RANGE_DAYS = 186;
const DEFAULT_RANGE_DAYS = 30;

// Cómo puede terminar un caso, en el orden en que se muestran.
const OUTCOMES = [
  { key: "appointment-taken", color: "#67c23a" },
  { key: "previously-assigned", color: "#2a9d8f" },
  { key: "desisted", color: "#f56c6c" },
  { key: "no-answer", color: "#e6a23c" },
  { key: "closed", color: "#909399" },
];

// Estado actual de las órdenes emitidas en el período.
const EMITTED_STATUSES = [
  "new",
  "callback",
  "pending-documentation",
  "appointment-taken",
  "previously-assigned",
  "desisted",
  "no-answer",
  "contacted",
  "appointment-proposed",
  "closed",
];

export default {
  name: "OrdersCrmStats",

  components: { Chart, Spinner },

  props: {
    coverages: { type: Array, default: () => [] },
  },

  data() {
    return {
      loading: false,
      loadSeq: 0,
      loadDebounce: null,
      stats: null,
      pickerMinDate: null,
      filters: {
        dateRange: [
          format(subDays(new Date(), DEFAULT_RANGE_DAYS), "yyyy-MM-dd"),
          format(new Date(), "yyyy-MM-dd"),
        ],
        type: "",
        coverages: [],
      },
    };
  },

  computed: {
    outcomes() {
      return OUTCOMES.map((outcome) => ({
        ...outcome,
        label: statusLabel(outcome.key),
      }));
    },

    rangePickerOptions() {
      return {
        onPick: ({ minDate }) => {
          this.pickerMinDate = minDate;
        },
        disabledDate: (date) => {
          if (date > new Date()) return true;
          if (!this.pickerMinDate) return false;
          const limit = addDays(this.pickerMinDate, MAX_RANGE_DAYS);
          const floor = subDays(this.pickerMinDate, MAX_RANGE_DAYS);
          return date > limit || date < floor;
        },
      };
    },

    emittedRows() {
      if (!this.stats) return [];
      const byStatus = this.stats.emitted.byStatus || {};
      return EMITTED_STATUSES.filter((status) => byStatus[status]).map(
        (status) => ({
          status,
          label: statusLabel(status),
          cls: STATUS_META[status]?.cls || "",
          count: byStatus[status],
        })
      );
    },

    weeks() {
      return this.stats?.weeks || [];
    },

    chartSeries() {
      return this.outcomes.map((outcome) => ({
        name: outcome.label,
        data: this.weeks.map((week) => week.outcomes[outcome.key] || 0),
      }));
    },

    chartOptions() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: this.outcomes.map((outcome) => outcome.color),
        plotOptions: { bar: { columnWidth: "55%" } },
        dataLabels: { enabled: false },
        legend: { position: "top" },
        xaxis: { categories: this.weeks.map((week) => this.weekLabel(week)) },
        yaxis: {
          forceNiceScale: true,
          labels: { formatter: (v) => Math.round(v) },
        },
      };
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        clearTimeout(this.loadDebounce);
        this.loadDebounce = setTimeout(() => this.load(), 350);
      },
    },
  },

  created() {
    this.load();
  },

  methods: {
    buildParams() {
      const [from, to] = this.filters.dateRange || [];
      const params = {};
      // "yyyy-MM-dd" se arma en hora local para no perder el día en ART.
      if (from) {
        const [y, m, d] = from.split("-").map(Number);
        params.from = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
      }
      if (to) {
        const [y, m, d] = to.split("-").map(Number);
        params.to = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
      }
      if (this.filters.type) params.type = this.filters.type;
      if (this.filters.coverages.length)
        params.coverage = this.filters.coverages;
      return params;
    },

    async load() {
      const seq = ++this.loadSeq;
      this.loading = true;
      try {
        const stats = await ordersCrmService.stats(this.buildParams());
        if (seq !== this.loadSeq) return;
        this.stats = stats;
      } catch (error) {
        if (seq !== this.loadSeq) return;
        console.error("Error loading orders CRM stats:", error);
        this.$message.error("Error al cargar las estadísticas");
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },

    percent(value) {
      if (value === null || value === undefined) return "—";
      return `${Math.round(value * 100)}%`;
    },

    count(outcomes, key) {
      return outcomes?.[key] || 0;
    },

    weekLabel(week) {
      return `Sem. ${format(parseISO(week.week), "dd/MM")}`;
    },

    closedInWeek(week) {
      return Object.values(week.outcomes).reduce((sum, n) => sum + n, 0);
    },
  },
};
</script>

<template lang="pug">
div
  .filters-card
    .filters-card__field.filters-card__field--wide
      label.filters-card__label Período (hasta 6 meses)
      el-date-picker(
        v-model="filters.dateRange"
        type="daterange"
        value-format="yyyy-MM-dd"
        range-separator="a"
        start-placeholder="Desde"
        end-placeholder="Hasta"
        :picker-options="rangePickerOptions"
        :clearable="false"
      )

    .filters-card__field
      label.filters-card__label Tipo
      el-select(v-model="filters.type" placeholder="Todos")
        el-option(value="" label="Todos")
        el-option(value="lab" label="Laboratorios")
        el-option(value="images,practices" label="Prescripciones")

    .filters-card__field
      label.filters-card__label Cobertura
      el-select(
        v-model="filters.coverages"
        placeholder="Todas"
        multiple
        collapse-tags
        clearable
        filterable
      )
        el-option(
          v-for="coverage in coverages"
          :key="coverage.key"
          :value="coverage.key"
          :label="coverage.label"
        )

  spinner(v-if="loading && !stats")

  template(v-if="stats")
    .counters(:class="{ 'is-refreshing': loading }")
      .counter-card
        .counter-card__body
          span.counter-card__value {{ stats.emitted.total }}
          span.counter-card__label Órdenes emitidas en el período
      .counter-card
        .counter-card__body
          span.counter-card__value {{ stats.managed.cases }}
          span.counter-card__label Casos gestionados
      .counter-card.is-success
        .counter-card__body
          span.counter-card__value {{ count(stats.managed.outcomes, 'appointment-taken') }}
          span.counter-card__label Turno asignado
      .counter-card.is-success
        .counter-card__body
          span.counter-card__value {{ percent(stats.managed.successRate) }}
          span.counter-card__label Tasa de éxito
      .counter-card
        .counter-card__body
          span.counter-card__value {{ count(stats.managed.outcomes, 'previously-assigned') }}
          span.counter-card__label Previamente asignado
      .counter-card.is-muted
        .counter-card__body
          span.counter-card__value {{ stats.managed.inProgress }}
          span.counter-card__label En gestión

    p.stats-note
      | Tasa de éxito: turnos asignados sobre los casos que resolvió el Contact
      | (Turno asignado + Desiste + No responde). "Previamente asignado" no cuenta,
      | porque ese turno no lo consiguió la gestión. Cada caso se atribuye a quien
      | hizo su última gestión dentro del período.

    .stats-card
      h3.stats-card__title Por operador
      p.stats-empty(v-if="!stats.operators.length") No hubo gestiones en el período.
      table.stats-table(v-else)
        thead
          tr
            th Operador
            th Casos
            th Gestiones
            th(v-for="outcome in outcomes" :key="outcome.key") {{ outcome.label }}
            th En gestión
            th Tasa de éxito
        tbody
          tr(v-for="row in stats.operators" :key="row.id || row.name")
            td
              strong {{ row.name }}
            td {{ row.cases }}
            td {{ row.actions }}
            td(v-for="outcome in outcomes" :key="outcome.key") {{ count(row.outcomes, outcome.key) }}
            td {{ row.inProgress }}
            td
              strong {{ percent(row.successRate) }}

    .stats-card
      h3.stats-card__title Casos cerrados por semana
      p.stats-empty(v-if="!weeks.length") Sin datos en el período.
      template(v-else)
        chart(
          type="bar"
          height="300"
          :options="chartOptions"
          :series="chartSeries"
        )
        table.stats-table
          thead
            tr
              th Semana
              th Órdenes emitidas
              th Cerrados
              th(v-for="outcome in outcomes" :key="outcome.key") {{ outcome.label }}
          tbody
            tr(v-for="week in weeks" :key="week.week")
              td {{ weekLabel(week) }}
              td {{ week.emitted }}
              td {{ closedInWeek(week) }}
              td(v-for="outcome in outcomes" :key="outcome.key") {{ count(week.outcomes, outcome.key) }}

    .stats-card
      h3.stats-card__title Estado actual de las órdenes emitidas en el período
      .emitted
        span.emitted__item(v-for="row in emittedRows" :key="row.status")
          span.status-badge(:class="row.cls") {{ row.label }}
          strong {{ row.count }}
</template>

<style lang="scss" scoped>
.filters-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  background: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 1.1rem 1.2rem;
  margin-bottom: 1.25rem;
}

.filters-card__field {
  display: flex;
  flex-direction: column;
  flex: 1 1 160px;
  min-width: 160px;

  ::v-deep .el-input,
  ::v-deep .el-select,
  ::v-deep .el-date-editor {
    width: 100%;
  }
}

.filters-card__field--wide {
  flex: 2 1 280px;
}

.filters-card__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: $lynch;
  margin-bottom: 0.3rem;
}

.counters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.75rem;

  &.is-refreshing {
    opacity: 0.45;
  }
}

.counter-card {
  display: flex;
  align-items: center;
  background: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 0.9rem 1.4rem;
  min-width: 150px;
  flex: 1 1 150px;
}

.counter-card.is-success .counter-card__value {
  color: #67c23a;
}

.counter-card.is-muted .counter-card__value {
  color: $lynch;
}

.counter-card__body {
  display: flex;
  flex-direction: column;
}

.counter-card__value {
  font-size: 1.6rem;
  font-weight: bold;
  color: $primary-alt;
  line-height: 1.1;
}

.counter-card__label {
  font-size: 0.8rem;
  color: $lynch;
}

.stats-note {
  font-size: 0.8rem;
  color: $lynch;
  margin: 0 0 1.25rem;
}

.stats-card {
  background: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 1.1rem 1.2rem;
  margin-bottom: 1.25rem;
  overflow-x: auto;
}

.stats-card__title {
  font-size: 0.85rem;
  font-weight: bold;
  color: $lynch;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 0.75rem;
}

.stats-empty {
  color: $lynch;
  margin: 0;
}

.stats-table {
  width: 100%;
  font-size: 0.9rem;

  th,
  td {
    text-align: right;
    white-space: nowrap;
    padding: 0.45rem 0.6rem;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }
}

.emitted {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
}

.emitted__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;

  &.is-new {
    background-color: #fdf6ec;
    color: #e6a23c;
  }

  &.is-callback {
    background-color: #f0f9ff;
    color: #409eff;
  }

  &.is-pending {
    background-color: #f5f0fe;
    color: #8e5fd6;
  }

  &.is-taken {
    background-color: #f0f9eb;
    color: #67c23a;
  }

  &.is-previous {
    background-color: #e8f6f5;
    color: #2a9d8f;
  }

  &.is-desisted,
  &.is-no-answer {
    background-color: #fef0f0;
    color: #f56c6c;
  }

  &.is-legacy {
    background-color: #f4f4f5;
    color: #909399;
  }
}
</style>
